import {
  DEFAULT_WALL_THICKNESS_CM,
  FloorplanMeasurementSystem,
} from '../constants/venueLayout'
import type { FloorplanDocument } from '../venueLayout'

const MIN_CANVAS_ROOM_SIZE = 8

type FloorplanRoom = FloorplanDocument['shell']['rooms'][number]

function distance2d(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(bx - ax, by - ay)
}

function getCalibrationDistanceCm(doc: FloorplanDocument): number | null {
  const calibration = doc.calibration
  if (!calibration) return null
  const distanceCm = calibration.distanceCm
  if (Number.isFinite(distanceCm) && (distanceCm ?? 0) > 0) {
    return distanceCm ?? null
  }
  return null
}

function toNonNegativeCm(value: number | null | undefined): number | null {
  if (!Number.isFinite(value)) return null
  if ((value ?? 0) < 0) return null
  return value as number
}

export function getCanvasUnitsPerCm(doc: FloorplanDocument): number {
  if (!doc.calibration) return 1
  const [[ax, ay], [bx, by]] = [doc.calibration.pointA, doc.calibration.pointB]
  const canvasDistance = distance2d(ax, ay, bx, by)
  if (!(canvasDistance > 0) || !Number.isFinite(canvasDistance)) return 1
  const distanceCm = getCalibrationDistanceCm(doc)
  if (!(distanceCm && Number.isFinite(distanceCm) && distanceCm > 0)) return 1
  const ratio = canvasDistance / distanceCm
  return Number.isFinite(ratio) && ratio > 0 ? ratio : 1
}

export function cmToCanvasUnits(cm: number, doc: FloorplanDocument): number {
  return cm * getCanvasUnitsPerCm(doc)
}

export function canvasUnitsToCm(
  canvasUnits: number,
  doc: FloorplanDocument
): number {
  const ratio = getCanvasUnitsPerCm(doc)
  if (!(ratio > 0) || !Number.isFinite(ratio)) return canvasUnits
  return canvasUnits / ratio
}

export function getWallThicknessCanvas(
  wall: { thicknessCm?: number | null },
  doc: FloorplanDocument
): number {
  return cmToCanvasUnits(
    Math.max(0.5, wall.thicknessCm ?? DEFAULT_WALL_THICKNESS_CM),
    doc
  )
}

export function getWallHeightCanvas(
  wall: { heightCm?: number | null },
  doc: FloorplanDocument
): number {
  return cmToCanvasUnits(Math.max(0, wall.heightCm ?? 0), doc)
}

export function getObjectHeightCanvas(
  objectRow: { heightCm?: number | null },
  doc: FloorplanDocument
): number {
  return cmToCanvasUnits(Math.max(0, objectRow.heightCm ?? 0), doc)
}

function getRoomLocalBounds(room: FloorplanRoom): {
  width: number
  height: number
} {
  const xs: number[] = []
  const ys: number[] = []
  for (let index = 0; index < room.polygon.length - 1; index += 2) {
    xs.push(room.polygon[index] ?? 0)
    ys.push(room.polygon[index + 1] ?? 0)
  }
  if (xs.length === 0 || ys.length === 0) return { width: 0, height: 0 }
  return {
    width: Math.max(0, Math.max(...xs) - Math.min(...xs)),
    height: Math.max(0, Math.max(...ys) - Math.min(...ys)),
  }
}

export function getRoomDimensionsCm(args: {
  doc: FloorplanDocument
  room: FloorplanRoom
}): { widthCm: number; heightCm: number } {
  const bounds = getRoomLocalBounds(args.room)
  return {
    widthCm:
      args.room.widthCm ?? Math.max(1, canvasUnitsToCm(bounds.width, args.doc)),
    heightCm:
      args.room.heightCm ??
      Math.max(1, canvasUnitsToCm(bounds.height, args.doc)),
  }
}

export function buildRoomCanvasPolygon(args: {
  doc: FloorplanDocument
  widthCm: number
  heightCm: number
}): number[] {
  const width = Math.max(
    MIN_CANVAS_ROOM_SIZE,
    cmToCanvasUnits(Math.max(1, args.widthCm), args.doc)
  )
  const height = Math.max(
    MIN_CANVAS_ROOM_SIZE,
    cmToCanvasUnits(Math.max(1, args.heightCm), args.doc)
  )
  return [0, 0, width, 0, width, height, 0, height]
}

export function normalizeFloorplanMeasurements(
  doc: FloorplanDocument
): FloorplanDocument {
  const distanceCm = getCalibrationDistanceCm(doc)
  const normalizedCalibration = doc.calibration
    ? {
        pointA: doc.calibration.pointA,
        pointB: doc.calibration.pointB,
        distanceCm: distanceCm ?? 1,
      }
    : null

  const nextDocBase: FloorplanDocument = {
    ...doc,
    measurementSystem:
      doc.measurementSystem ?? FloorplanMeasurementSystem.METRIC,
    calibration: normalizedCalibration,
  }

  return {
    ...nextDocBase,
    shell: {
      ...nextDocBase.shell,
      rooms: nextDocBase.shell.rooms.map((room) => {
        const { widthCm, heightCm } = getRoomDimensionsCm({
          doc: nextDocBase,
          room,
        })
        return {
          ...room,
          widthCm,
          heightCm,
          walls: room.walls.map((wall) => {
            return {
              ...wall,
              thicknessCm: wall.thicknessCm ?? null,
              heightCm: toNonNegativeCm(wall.heightCm),
            }
          }),
          partitions: room.partitions.map((partition) => {
            return {
              ...partition,
              thicknessCm: partition.thicknessCm ?? null,
              heightCm: toNonNegativeCm(partition.heightCm),
            }
          }),
          openings: room.openings.map((opening) => ({
            ...opening,
            widthCm: opening.widthCm ?? null,
          })),
        }
      }),
      walls: nextDocBase.shell.walls.map((wall) => ({
        ...wall,
        thicknessCm: wall.thicknessCm ?? null,
        heightCm: toNonNegativeCm(wall.heightCm),
      })),
      openings: nextDocBase.shell.openings.map((opening) => ({
        ...opening,
        widthCm: opening.widthCm ?? null,
      })),
      fixtures:
        nextDocBase.shell.fixtures?.map((fixture) => ({
          ...fixture,
          widthCm: fixture.widthCm ?? null,
          heightCm: fixture.heightCm ?? null,
        })) ?? nextDocBase.shell.fixtures,
    },
    objects: nextDocBase.objects.map((objectRow) => {
      if (objectRow.type === 'table') {
        return {
          ...objectRow,
          widthCm: objectRow.widthCm ?? null,
          depthCm: objectRow.depthCm ?? null,
          heightCm: toNonNegativeCm(objectRow.heightCm),
        }
      }
      if (objectRow.type === 'section') {
        return {
          ...objectRow,
          heightCm: toNonNegativeCm(objectRow.heightCm),
        }
      }
      if (objectRow.type === 'decor') {
        return {
          ...objectRow,
          widthCm: objectRow.widthCm ?? null,
          heightCm: toNonNegativeCm(objectRow.heightCm),
        }
      }
      if (objectRow.type === 'lightingZone') {
        return {
          ...objectRow,
          radiusCm: objectRow.radiusCm ?? null,
        }
      }
      return objectRow
    }),
  }
}
